import { Grid, Paper } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { mainViewWidthState } from '~/state/atoms/toolsAtoms';
import {
    expandDoseStatisticsTableState,
    GridLayoutMode,
    gridLayoutModeState,
    showDoseStatisticsTableFullScreenState,
    showDoseStatisticsTableState,
    showDVHViewComponentFullScreenState,
    showDVHViewComponentState,
    showFullscreenState,
} from '~/state/atoms/viewerAtoms';
import DVHLineChartComponent from '../dvh-line-chart';
import TablePanel from '../table-panel';
import Viewport from './Viewport';

interface ViewportGridLayoutProps {
    main: {
        view: PlaneView | null;
        canvas: React.RefObject<HTMLCanvasElement | null>;
        canvas2D: React.RefObject<HTMLCanvasElement | null>;
        orientation: Orientations;
    };
    side: {
        upper: {
            view: PlaneView | null;
            canvas: React.RefObject<HTMLCanvasElement | null>;
            canvas2D: React.RefObject<HTMLCanvasElement | null>;
            orientation: Orientations;
        };
        lower: {
            view: PlaneView | null;
            canvas: React.RefObject<HTMLCanvasElement | null>;
            canvas2D: React.RefObject<HTMLCanvasElement | null>;
            orientation: Orientations;
        };
    };
}

// Z-index for DVH/chart panel; intentionally above the step toolbar layer (10).
const DVH_PANEL_Z_INDEX = 11;
// Z-index for DVH table; should be above the DVH/chart panel to ensure it is fully visible when expanded.
const DVH_TABLE_Z_INDEX = 12;

const TABLE_PANEL_APPBAR_HEIGHT = 55;
const TABLE_PANEL_DEFAULT_HEIGHT = 335;
const TABLE_PANEL_MIN_HEIGHT = 200;
const TABLE_PANEL_MAX_HEIGHT = 600;
const RESIZE_HANDLE_HEIGHT = 6;

const convertMainViewWidthFromPercentToGridValue = (
    mainViewWidthPercent: number
): number => (Math.max(0, Math.min(100, mainViewWidthPercent)) / 100) * 12;

const ViewportGridLayout = ({ main, side }: ViewportGridLayoutProps) => {
    const gridLayoutMode = useRecoilValue(gridLayoutModeState);
    const currentDose = useRecoilValue(currentDoseState);
    const showDVHViewComponentFullScreen = useRecoilValue(
        showDVHViewComponentFullScreenState
    );
    const showDVHViewComponent = useRecoilValue(showDVHViewComponentState);
    const showDoseStatisticsTable = useRecoilValue(
        showDoseStatisticsTableState
    );
    const expandDoseStatisticsTable = useRecoilValue(
        expandDoseStatisticsTableState
    );
    const showDoseStatisticsTableFullScreen = useRecoilValue(
        showDoseStatisticsTableFullScreenState
    );
    const showFullscreen = useRecoilValue(showFullscreenState);
    const mainViewWidth = useRecoilValue(mainViewWidthState);

    const [tablePanelHeight, setTablePanelHeight] = useState(
        TABLE_PANEL_DEFAULT_HEIGHT
    );
    const isDraggingRef = useRef(false);
    const resizeHandleRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);
    const startHeightRef = useRef(0);
    const tablePanelHeightRef = useRef(tablePanelHeight);
    tablePanelHeightRef.current = tablePanelHeight;

    const startResizeDrag = useWindowMouseDrag(
        (moveEvent) => {
            const deltaY = startYRef.current - moveEvent.clientY;
            const newHeight = Math.min(
                TABLE_PANEL_MAX_HEIGHT,
                Math.max(
                    TABLE_PANEL_MIN_HEIGHT,
                    startHeightRef.current + deltaY
                )
            );
            setTablePanelHeight(newHeight);
        },
        () => {
            isDraggingRef.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            if (resizeHandleRef.current) {
                resizeHandleRef.current.style.background = 'transparent';
            }
        }
    );

    const handleResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            isDraggingRef.current = true;
            startYRef.current = e.clientY;
            startHeightRef.current = tablePanelHeightRef.current;
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
            startResizeDrag(e);
        },
        [startResizeDrag]
    );

    if (gridLayoutMode === GridLayoutMode.Default) {
        return (
            <>
                <Grid
                    container
                    spacing={1}
                    px={1}
                    pb={0.5}
                    position="relative"
                    overflow="hidden"
                    sx={{
                        flex: '1 1 0',
                        minHeight: 0,
                        position: showDoseStatisticsTableFullScreen
                            ? 'absolute'
                            : 'static',
                        left: showDoseStatisticsTableFullScreen ? '-100%' : '',
                    }}
                >
                    <Grid
                        item
                        xs={
                            showFullscreen
                                ? 12
                                : convertMainViewWidthFromPercentToGridValue(
                                      mainViewWidth
                                  )
                        }
                        position={
                            showDVHViewComponentFullScreen
                                ? 'absolute'
                                : 'static'
                        }
                        left={showDVHViewComponentFullScreen ? '-100%' : ''}
                    >
                        <Paper
                            sx={{
                                background: 'black',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <Viewport
                                view={main.view}
                                viewportOrientation={main.orientation}
                                canvas={main.canvas}
                                canvas2D={main.canvas2D}
                            />
                        </Paper>
                    </Grid>
                    <Grid
                        item
                        xs={
                            showDVHViewComponentFullScreen
                                ? 12
                                : 12 -
                                  convertMainViewWidthFromPercentToGridValue(
                                      mainViewWidth
                                  )
                        }
                        container
                        spacing={1}
                        direction={
                            showDVHViewComponent || showDoseStatisticsTable
                                ? 'column'
                                : 'row'
                        }
                        position={showFullscreen ? 'absolute' : 'relative'}
                        left={showFullscreen ? '-100%' : ''}
                        data-testid="side-viewports"
                    >
                        {showDVHViewComponent && currentDose ? (
                            <Grid
                                item
                                xs={12}
                                md={showDVHViewComponentFullScreen ? 12 : 6}
                            >
                                <Paper
                                    sx={{
                                        background: 'black',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        zIndex: DVH_PANEL_Z_INDEX,
                                    }}
                                >
                                    <DVHLineChartComponent />
                                </Paper>
                            </Grid>
                        ) : null}

                        <Grid
                            item
                            container
                            spacing={1}
                            md={showDVHViewComponent ? 6 : 12}
                            position={
                                showDVHViewComponentFullScreen
                                    ? 'absolute'
                                    : 'relative'
                            }
                            left={showDVHViewComponentFullScreen ? '-100%' : ''}
                        >
                            {[side.upper, side.lower].map((s, i) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={showDVHViewComponent ? 3 : 12}
                                    md={
                                        showDVHViewComponent ||
                                        showDoseStatisticsTable
                                            ? 6
                                            : 12
                                    }
                                    key={i}
                                >
                                    <Paper
                                        sx={{
                                            background: 'black',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Viewport
                                            view={s.view}
                                            viewportOrientation={s.orientation}
                                            canvas={s.canvas}
                                            canvas2D={s.canvas2D}
                                        />
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
                {showDoseStatisticsTable && currentDose ? (
                    <Grid
                        container
                        item
                        position="relative"
                        sx={{
                            overflow: 'hidden',
                            flexShrink: 0,
                            height: expandDoseStatisticsTable
                                ? TABLE_PANEL_APPBAR_HEIGHT
                                : showDoseStatisticsTableFullScreen
                                  ? undefined
                                  : tablePanelHeight,
                            zIndex: DVH_TABLE_Z_INDEX,
                            ...(showDoseStatisticsTableFullScreen && {
                                flex: 1,
                                minHeight: 0,
                            }),
                        }}
                    >
                        {!expandDoseStatisticsTable &&
                            !showDoseStatisticsTableFullScreen && (
                                <div
                                    ref={resizeHandleRef}
                                    onMouseDown={handleResizeMouseDown}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: RESIZE_HANDLE_HEIGHT,
                                        cursor: 'ns-resize',
                                        zIndex: DVH_TABLE_Z_INDEX + 1,
                                        background: 'transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                        (
                                            e.currentTarget as HTMLElement
                                        ).style.background =
                                            'rgba(144, 202, 249, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isDraggingRef.current) {
                                            (
                                                e.currentTarget as HTMLElement
                                            ).style.background = 'transparent';
                                        }
                                    }}
                                />
                            )}
                        <TablePanel />
                    </Grid>
                ) : null}
            </>
        );
    }
    return (
        <Grid container spacing={1} px={1} sx={{ flex: 1 }}>
            {[main, side.upper, side.lower].map((s, i) => (
                <Grid item xs={4} key={`row-viewport-${s.orientation}-${i}`}>
                    <Paper
                        sx={{
                            background: 'black',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Viewport
                            view={s.view}
                            viewportOrientation={s.orientation}
                            canvas={s.canvas}
                            canvas2D={s.canvas2D}
                        />
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );
};

export default ViewportGridLayout;
